import { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  Alert,
  AlertColor,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

interface ConfigTest {
  name: string;
  value: string | boolean;
  status: 'success' | 'warning' | 'error';
  message?: string;
}

export const DebugPanel = () => {
  const [tests, setTests] = useState<ConfigTest[]>([]);
  const [loading, setLoading] = useState(false);
  const [customUrl, setCustomUrl] = useState('');

  const keycloakUrl = (import.meta as any).env.VITE_KEYCLOAK_URL;
  const realm = (import.meta as any).env.VITE_KEYCLOAK_REALM;
  const clientId = (import.meta as any).env.VITE_KEYCLOAK_CLIENT_ID;
  const apiUrl = (import.meta as any).env.VITE_API_URL;

  const runTests = async () => {
    setLoading(true);
    const newTests: ConfigTest[] = [];

    // Test Keycloak URL
    try {
      const response = await fetch(keycloakUrl, { mode: 'no-cors' });
      newTests.push({
        name: 'Keycloak URL Reachable',
        value: keycloakUrl,
        status: 'success',
        message: 'Keycloak server is reachable',
      });
    } catch (error) {
      newTests.push({
        name: 'Keycloak URL Reachable',
        value: keycloakUrl,
        status: 'error',
        message: String(error),
      });
    }

    // Test Realm
    newTests.push({
      name: 'Realm Configured',
      value: realm,
      status: 'success',
    });

    // Test Client ID
    newTests.push({
      name: 'Client ID Configured',
      value: clientId,
      status: 'success',
    });

    // Test API URL
    try {
      const response = await fetch(`${apiUrl}/health`);
      const data = await response.json();
      newTests.push({
        name: 'Backend API Reachable',
        value: apiUrl,
        status: 'success',
        message: `Status: ${data.status}`,
      });
    } catch (error) {
      newTests.push({
        name: 'Backend API Reachable',
        value: apiUrl,
        status: 'error',
        message: String(error),
      });
    }

    // Test well-known config for various paths
    const paths = [
      `${keycloakUrl}/realms/${realm}/.well-known/openid-configuration`,
      `${keycloakUrl}/auth/realms/${realm}/.well-known/openid-configuration`,
    ];

    for (const path of paths) {
      try {
        const response = await fetch(path);
        if (response.ok) {
          newTests.push({
            name: 'OpenID Config Found',
            value: path,
            status: 'success',
            message: 'Successfully found OpenID configuration',
          });
          break;
        }
      } catch (error) {
        // Continue to next path
      }
    }

    setTests(newTests);
    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <h2>Keycloak Configuration Debug Panel</h2>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={runTests}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Run Diagnostics'}
          </Button>
        </Box>

        {tests.length > 0 && (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Check</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test, idx) => (
                <TableRow key={idx}>
                  <TableCell>{test.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                    {String(test.value).substring(0, 50)}...
                  </TableCell>
                  <TableCell>
                    <Alert
                      severity={test.status}
                      sx={{ py: 0.5, px: 1, display: 'inline-flex' }}
                    >
                      {test.status}
                    </Alert>
                  </TableCell>
                  <TableCell>{test.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <h3>Configuration</h3>
          <pre style={{ margin: 0, fontSize: '0.9em', overflow: 'auto' }}>
            VITE_KEYCLOAK_URL: {keycloakUrl}
            {'\n'}
            VITE_KEYCLOAK_REALM: {realm}
            {'\n'}
            VITE_KEYCLOAK_CLIENT_ID: {clientId}
            {'\n'}
            VITE_API_URL: {apiUrl}
          </pre>
        </Box>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
          <h3>For Older Keycloak (pre-17)</h3>
          <p>
            If your Keycloak uses the `/auth/realms` path structure, make sure
            your VITE_KEYCLOAK_URL includes that path:
          </p>
          <pre style={{ margin: '10px 0', fontFamily: 'monospace', fontSize: '0.9em' }}>
            VITE_KEYCLOAK_URL=https://keycloak.example.com/auth
          </pre>
        </Box>
      </Paper>
    </Container>
  );
};

export default DebugPanel;
